import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";
import { Field, FieldArray } from "redux-form";
import _ from "lodash";
import {
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import { lastFieldChanged } from "@registrationaction";
import {
  renderFieldText,
  RenderFieldPhone,
  renderHiddenFieldText,
  renderFieldTextarea,
  renderFieldSelect,
  renderFieldCheckbox,
  renderFieldRadio,
  renderSingleCheckbox,
  renderDatePicker,
  renderAutoSelect,
} from "@basecomponent";
import {
  TICKET_FOLDER,
  COMPANY_PREFIX,
  NO_TRACK,
  DEFAULT_INTEREST_TAG_NAME,
} from "@constant";
import { validateUrl } from "@utilscommon";
import AnyFilesDropOnly from "@anyfiledroponly";
import LocaleStrings from "@language";
import ImageCropper from "@imagecropper";

const renderGroupMembers = ({ fields, form, categoryResponse, autofill }) => {
  if (fields.length === 0) {
    fields.push({});
  }

  const fieldOrder = [
    "file",
    "textinput",
    "dropdown",
    "textarea",
    "datepicker",
  ];

  const orderedFields = [...form.groupfields].sort(
    (a, b) => fieldOrder.indexOf(a.key) - fieldOrder.indexOf(b.key)
  );

  const categories =
    categoryResponse?.map((item) => ({ value: item.name, text: item.name })) ||
    [];
  const onImageSave = (img, name) => {
    autofill(name, img);
  };

  return form.type === "abstract" ? (
    <div className="space-y-1 2xs:space-y-1.5">
      <label className="control-label-top col-span-12">
        {form?.groupfields?.[0]?.label}
      </label>
      <p className="custom-text-secondary-sm-normal col-span-12">
        {form?.groupfields?.[1]?.label}
      </p>
    </div>
  ) : (
    <div className="form-group row ">
      <label className="control-label-top col-sm-12">{form.label}</label>
      <div>
        {fields.map((member, index) => {
          const currentRow = fields.get(index) || {};
          const selectedCategory = currentRow?.productcategory || "";

          const subcategories =
            categoryResponse.find((c) => c.name === selectedCategory)
              ?.master_subcategory_by_categoryid || [];

          const subcategoryOptions = subcategories.map((sub) => ({
            value: sub.name,
            text: sub.name,
          }));

          // Check if `file` exists in orderedFields
          const hasFileField = orderedFields.some((gf) => gf.key === "file");

          return (
            <div key={index} className="w-full">
              <div className="w-full p-3 border rounded-2xl mb-4">
                <div
                  className={`flex flex-wrap ${
                    hasFileField ? "md:flex-nowrap gap-4" : ""
                  }`}
                >
                  {/* Render file separately if present */}
                  {hasFileField && (
                    <div className="w-full md:w-1/3 flex-shrink-0">
                      {orderedFields
                        .filter((gf) => gf.key === "file")
                        .map((gf, gfIndex) => (
                          <div key={gfIndex} className="w-full">
                            <div className="flex flex-col items-start justify-start rounded-lg p-4">
                              <ImageCropper
                                displaySize={{ width: 200, height: 200 }}
                                requiredSize={{ width: 200, height: 200 }}
                                cropperSize={{ width: 160, height: 160 }}
                                border={10}
                                onImageSave={(img) =>
                                  onImageSave(img, `${member}.${gf.name}`)
                                }
                                // imagepath={imagePreviewUrl}
                                displayText={LocaleStrings.kFileDragDropText}
                                fileSizeText={LocaleStrings.kFileMinSizeText}
                                imageType="jpg"
                              />
                              <Field
                                name={`${member}.${gf.name}`}
                                type="text"
                                component={renderHiddenFieldText}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Other fields */}
                  <div className="flex-1 flex flex-wrap">
                    {orderedFields
                      .filter((gf) => gf.key !== "file")
                      .map((gf, gfIndex) => {
                        if (gf.key === "textinput") {
                          return (
                            <div key={gfIndex} className="w-full mb-3 px-1">
                              <Field
                                name={`${member}.${gf.name}`}
                                component={renderFieldText}
                                label={gf.label}
                                type="text"
                                placeholder={`Enter ${gf.label}`}
                                maxLength={gf.maxlen}
                                mandatory={gf?.required ?? false}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                          );
                        }

                        if (gf.key === "dropdown") {
                          const options =
                            gf.id === "productcategory"
                              ? categories
                              : subcategoryOptions;

                          return (
                            <div
                              key={gfIndex}
                              className="w-full md:w-1/2 mb-3 px-1"
                            >
                              <Field
                                name={`${member}.${gf.name}`}
                                label={gf.label}
                                component={renderFieldSelect}
                                mandatory={gf?.required ?? false}
                                options={options}
                              />
                            </div>
                          );
                        }

                        if (gf.key === "textarea") {
                          return (
                            <div key={gfIndex} className="w-full mb-3 px-1">
                              <Field
                                name={`${member}.${gf.name}`}
                                label={gf.label}
                                component={renderFieldTextarea}
                                rows="3"
                                maxLength={gf.maxlen}
                                mandatory={gf?.required ?? false}
                                placeholder={`Enter ${gf.label}`}
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                          );
                        }

                        if (gf.key === "datepicker") {
                          return (
                            <div key={gfIndex} className="w-full mb-3 px-1">
                              <Field
                                name={`${member}.${gf.name}`}
                                label={gf.label}
                                component={renderDatePicker}
                                mandatory={gf?.required ?? false}
                                placeholder={`Enter ${gf.label}`}
                              />
                            </div>
                          );
                        }

                        return null;
                      })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Action Buttons */}
        <div className="w-full flex items-start justify-end">
          <div className="flex flex-row gap-2">
            <button
              type="button"
              onClick={() => fields.push({})}
              className="flex items-center justify-center w-8 h-8 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 transition"
            >
              +
            </button>
            {fields.length > 1 && (
              <button
                type="button"
                onClick={() => fields.remove(fields.length - 1)}
                className="flex items-center justify-center w-8 h-8 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 transition"
              >
                −
              </button>
            )}
          </div>
        </div>
      </div>{" "}
    </div>
  );
};

const FieldItem = (props) => {
  const location = useLocation();
  const {
    source = "",
    form,
    autofill,
    formValues,
    interestList,
    eventData,
    eventSettingsData,
    countryList,
    categoryResponse,
  } = props;
  const [userInterest, setUserInterest] = useState(
    formValues?.interest && Array.isArray(formValues?.interest)
      ? formValues?.interest
      : []
  );
  const [rerenderDom, setRerenderDom] = useState(0);

  useEffect(() => {
    let formKey = form?.key;
    let name = form?.name;
    let fieldtype = form?.fieldtype;

    if (
      formKey === "dynamicfield" &&
      (name === "interest" || fieldtype === "interest")
    ) {
      setUserInterest(
        formValues?.interest && Array.isArray(formValues?.interest)
          ? formValues?.interest
          : []
      );
    }
  }, [form]);
  // console.log("formValues :- ", formValues);
  // console.log("source :- ", source);
  // console.log("Form Field :- ", form);

  let params = location.search;

  let formKey = form?.key;
  let groupType = form?.type;
  let groupFields = form?.groupfields;
  let name = form?.name;
  let fieldtype = form?.fieldtype;
  let formLabel = form?.label;
  let isReadOnly = form?.isreadonly ?? false;
  let isHidden = form?.hidden ?? false;
  let isRequired = form?.required && !isReadOnly && !isHidden ? true : false;
  let isExclude = form?.exclude ?? false;
  let maxLength = form?.maxlen;
  let countrycode = form?.defaultvalue; // default country code checking
  let dropdowndisabled =
    countrycode && countrycode !== "" ? form?.isrestricted : false; // disabled country dropdownlist
  const { setting } = form || {};
  const type = setting?.type;
  const date = setting?.value;
  // console.log({ date });
  const minDate =
    type === "after" ? date : type === "between" ? date.split(" - ")?.[0] : "";
  const maxDate =
    type === "before" ? date : type === "between" ? date.split(" - ")?.[1] : "";

  // Checking if it registratiion form and signup sleected as phone number, then hide the phone no field from here as we are display this in fixed fields component
  if (
    source === "registration" &&
    (name === "phonenumber" || fieldtype === "phonenumber") &&
    formValues?.signupwith === "phonenumber"
  ) {
    isExclude = true;
  }
  let filetype = "";
  let accept = "";
  if (formKey === "file") {
    filetype = form?.filetype;
    // console.log("filetype :- ", filetype);
    if (filetype === "image") {
      accept = "image/*";
    } else if (filetype === "video") {
      accept = "video/*";
    } else if (filetype === "audio") {
      accept = "audio/*";
    } else if (filetype === "document") {
      accept = "application/*";
      // accept =
      //   "application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/zip";
    }
  }
  let dropdownOptions = [];
  if (formKey === "dropdown") {
    _.forEach(form?.options, (o) => {
      dropdownOptions.push({ value: o.value, label: o.text });
    });
  }

  let TCURL = validateUrl(eventSettingsData?.data?.termsandconditions)
    ? eventSettingsData?.data?.termsandconditions
    : `${TICKET_FOLDER}/terms-conditions${params}`;
  let PPURL = validateUrl(eventSettingsData?.data?.privacy)
    ? eventSettingsData?.data?.privacy
    : `${TICKET_FOLDER}/privacy-policy${params}`;

  const handleLastFieldChanged = (e) => {
    let value = "";
    if (
      (props.checkoutCompanyForm ?? [])?.length > 0 &&
      !_.includes(name, COMPANY_PREFIX)
    ) {
      autofill(`${COMPANY_PREFIX}${name}`, e?.target?.value);
    }
    if (
      formKey === "checkboxes" ||
      formKey === "datepicker" ||
      formKey === "file" ||
      formKey === "dynamicfield"
    ) {
      value = e;
    } else if (formKey === "singlecheckbox") {
      value = e?.target?.checked;
    } else if (formKey === "dropdown") {
      if (typeof e === "object") {
        value = e?.value;
      } else {
        value = e?.target?.value;
      }
    } else {
      value = e?.target?.value;
    }
    if (source === "ticketassign") {
      props.updatedLastFieldChanged(form, value); // Parent props function call
    } else {
      props.lastFieldChanged(name);
    }
  };

  const onFilesDrop = (data, name) => {
    // console.log("data :- ", data);
    // console.log("name :- ", name);

    let fileArray = formValues?.[name] ?? [];
    fileArray.push(data);
    // console.log("new files :- ", fileArray);

    autofill(`${name}`, fileArray);
    setRerenderDom((prev) => prev + 1);
    handleLastFieldChanged(fileArray);
  };

  const onFilesRemove = (index, name) => {
    let newFileArray = formValues?.[name] ?? [];
    _.pullAt(newFileArray, index);
    // console.log('newFileArray :- ', newFileArray)

    autofill(`${name}`, newFileArray?.length > 0 ? newFileArray : null);
    setRerenderDom((prev) => prev + 1);
    handleLastFieldChanged(newFileArray?.length > 0 ? newFileArray : null);
  };

  const renderFiles = () => {
    let fileArray = formValues?.[name] ?? [];
    let maxfile = form?.maxupload ?? 1;
    let existingFilesCount = fileArray?.length;

    // console.log("fileArray :- ", name, fileArray);
    // console.log("existingFilesCount :- ", existingFilesCount);
    // console.log("maxfile :- ", maxfile);

    return (
      <>
        {_.map(fileArray, (item, index) => {
          // console.log('index :- ', index);
          let fileName = "";
          if (item && item.file) {
            fileName = item?.filename;
          }

          let icon = <DocumentTextIcon className="w-5 h-5" />;
          if (fileName !== "") {
            let fileType = fileName.split(".").pop();

            if (
              fileType === "svg" ||
              fileType === "png" ||
              fileType === "jpg" ||
              fileType === "jpeg"
            ) {
              icon = <PhotoIcon className="w-5 h-5" />;
            } else if (fileType === "pdf") {
              icon = <DocumentTextIcon className="w-5 h-5" />;
            }
          }

          return (
            <div
              key={`${name}_file_${index}`}
              className={`uploaded-attachment-displaybox`}
            >
              <>{icon}</>
              <div className="file-name custom-text-primary-md-normal">
                {fileName}
              </div>
              <div className="flex items-center space-x-3">
                <TrashIcon
                  className="w-5 h-5 cursor-pointer"
                  onClick={() => onFilesRemove(index, name)}
                />
              </div>
            </div>
          );
        })}

        <div>
          {existingFilesCount < maxfile ? (
            <AnyFilesDropOnly
              onFileSave={(data) => onFilesDrop(data, name)}
              accept={accept}
              displayText={LocaleStrings.kFileDragDropText}
              className=""
            />
          ) : (
            <div className="div-input-field">
              <span className="custom-text-secondary-sm-medium">
                {LocaleStrings.kMaxFileUpload}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

  const handleUserInterest = (item) => {
    var array = userInterest;

    if (_.includes(array, item.interestid)) {
      var filter = _.filter(array, function (n) {
        return parseInt(n) !== parseInt(item.interestid);
      });
      array = filter;
    } else {
      array = [...array, item.interestid];
    }

    setUserInterest(array);
    props.autofill(`${name}`, array);
    handleLastFieldChanged(array);
  };

  const _renderInterests = () => {
    let allInterests = interestList?.interests;
    let categoryWiseInterests = interestList?.categorywise;
    // console.log({ interestList, form });

    if (!_.isEmpty(categoryWiseInterests)) {
      let allowedInterestCategory = []; // If blank means all category, This will only be work if category wise interest is added.
      if (form?.options) {
        allowedInterestCategory = _.map(form.options, (o) => {
          return o.value === NO_TRACK ? DEFAULT_INTEREST_TAG_NAME : o.value;
        });
      }
      // console.log({ allowedInterestCategory, categoryWiseInterests });
      let intCategory = categoryWiseInterests;
      if (!_.isEmpty(allowedInterestCategory)) {
        intCategory = {};
        allowedInterestCategory.forEach((c) => {
          if (Object.hasOwn(categoryWiseInterests, c)) {
            intCategory[c] = categoryWiseInterests[c];
          }
        });
      }
      // console.log({ intCategory });

      return _.map(intCategory, (interests, category) => {
        return (
          <div key={`int_cat_key_${category}`}>
            {/* Category Name */}
            {category === DEFAULT_INTEREST_TAG_NAME ? (
              ""
            ) : (
              <div className="custom-text-secondary-md-bold mb-2">
                {category}
              </div>
            )}
            {/* Interests List */}
            <div className="interest-section">
              {_.map(interests, (item, index) => (
                <div
                  key={`int_key_${index}`}
                  className={`custom-item-vcenter gap-1 ${
                    _.includes(userInterest, item.interestid)
                      ? "selected-interest"
                      : "interests"
                  }`}
                  onClick={() => handleUserInterest(item)}
                >
                  {item.interest}
                </div>
              ))}
            </div>
          </div>
        );
      });
    } else {
      return (
        <div className="interest-section">
          {_.map(allInterests, (item, index) => {
            return (
              <div
                key={`int_key_${index}`}
                className={`custom-item-vcenter gap-1 ${
                  _.includes(userInterest, item.interestid)
                    ? "selected-interest"
                    : "interests"
                }`}
                onClick={() => handleUserInterest(item)}
              >
                {item.interest}
              </div>
            );
          })}
        </div>
      );
    }
  };

  return (
    <>
      {/* Checkout Fields */}
      <div>
        {isHidden || isExclude ? (
          <></>
        ) : (
          <>
            {formKey === "textinput" || formKey === "numberinput" ? (
              <>
                {name === "phonenumber" ||
                fieldtype === "phonenumber" ||
                name === "whatsapp" ||
                fieldtype === "whatsapp" ||
                name === `${COMPANY_PREFIX}phonenumber` ||
                name === `${COMPANY_PREFIX}whatsapp` ? (
                  <Field
                    name={name}
                    label={formLabel}
                    type={"number"}
                    component={RenderFieldPhone}
                    mandatory={isRequired}
                    disabled={isReadOnly}
                    maxlength={maxLength}
                    eventid={eventData?.eventid}
                    onChange={(e) =>
                      handleLastFieldChanged({ target: { value: e, name } })
                    }
                    countrycode={countrycode}
                    countryList={countryList}
                    isrestricted={dropdowndisabled}
                  />
                ) : (
                  <Field
                    name={name}
                    label={formLabel}
                    type={formKey === "textinput" ? "text" : "number"}
                    component={renderFieldText}
                    mandatory={isRequired}
                    disabled={isReadOnly}
                    maxlength={maxLength}
                    onChange={handleLastFieldChanged}
                  />
                )}
              </>
            ) : formKey === "textarea" ? (
              <Field
                name={name}
                label={formLabel}
                component={renderFieldTextarea}
                mandatory={isRequired}
                disabled={isReadOnly}
                maxlength={maxLength}
                onChange={handleLastFieldChanged}
              />
            ) : formKey === "checkboxes" ? (
              <Field
                name={name}
                label={formLabel}
                component={renderFieldCheckbox}
                mandatory={isRequired}
                disabled={isReadOnly}
                opts={form?.options}
                onChange={handleLastFieldChanged}
              />
            ) : formKey === "radiobuttons" ? (
              <Field
                name={name}
                label={formLabel}
                component={renderFieldRadio}
                mandatory={isRequired}
                disabled={isReadOnly}
                opts={form?.options}
                onChange={handleLastFieldChanged}
              />
            ) : formKey === "dropdown" ? (
              <Field
                name={name}
                label={formLabel}
                placeholder={LocaleStrings.kSelectPlaceholder}
                component={
                  name === "country" || fieldtype === "country"
                    ? renderAutoSelect
                    : renderFieldSelect
                }
                mandatory={isRequired}
                disabled={isReadOnly}
                isMulti={false}
                options={dropdownOptions}
                onChange={handleLastFieldChanged}
              />
            ) : formKey === "datepicker" ? (
              <Field
                name={name}
                label={formLabel}
                component={renderDatePicker}
                mandatory={isRequired}
                disabled={isReadOnly}
                onChange={handleLastFieldChanged}
                maxDate={maxDate}
                minDate={minDate}
                pickertype={form?.pickertype ?? "single"}
              />
            ) : formKey === "hyperlink" ? (
              <a
                href={form?.link}
                target="blank"
                className="custom-text-theme-md-medium"
              >
                <div>{formLabel}</div>
              </a>
            ) : formKey === "label" ? (
              <label className="control-label-top">{formLabel}</label>
            ) : formKey === "paragraph" ? (
              <p className="custom-text-primary-md-normal">{formLabel}</p>
            ) : formKey === "singlecheckbox" ? (
              <>
                {name === "termsconditions" ||
                name === `${COMPANY_PREFIX}termsconditions` ? (
                  <Field
                    name={name}
                    htmllabel={`
                      <span>
                        ${formLabel}
                        <br />
                        <a href='${TCURL}' target='_blank'><u><b><span>${LocaleStrings.kTermsAndConditions}</span></b></u></a>
                        &emsp; | &emsp;
                        <a href='${PPURL}' target='_blank'><u><b><span>${LocaleStrings.KPrivacyPolicy}</span></b></u></a>
                      </span>`}
                    component={renderSingleCheckbox}
                    value="1"
                    type="checkbox"
                    mandatory={isRequired}
                    onChange={handleLastFieldChanged}
                  />
                ) : (
                  <Field
                    name={name}
                    label={formLabel}
                    component={renderSingleCheckbox}
                    value="1"
                    type="checkbox"
                    mandatory={isRequired}
                    onChange={handleLastFieldChanged}
                  />
                )}
              </>
            ) : formKey === "file" ? (
              <div className="space-y-1 2xs:space-y-1.5">
                <label className="control-label-top col-span-12">
                  {formLabel}{" "}
                  <span className="custom-text-secondary-sm-medium">
                    ({LocaleStrings.kMaxUploadText} : {form?.maxupload})
                  </span>
                  {isRequired ? <span className="label-mandatory">*</span> : ""}
                </label>
                {renderFiles()}
                <Field
                  name={`${name}`}
                  type="text"
                  component={renderHiddenFieldText}
                />
              </div>
            ) : formKey === "dynamicfield" ? (
              <>
                {name === "interest" || fieldtype === "interest" ? (
                  <div className="space-y-1 2xs:space-y-1.5">
                    <label className="control-label-top col-span-12">
                      {formLabel}
                      {isRequired ? (
                        <span className="label-mandatory">*</span>
                      ) : (
                        ""
                      )}
                    </label>
                    {_renderInterests()}
                    <Field
                      name={`${name}`}
                      type="text"
                      component={renderHiddenFieldText}
                    />
                  </div>
                ) : (
                  ""
                )}
              </>
            ) : formKey === "group" ? (
              <>
                <FieldArray
                  name={name}
                  component={renderGroupMembers}
                  props={{
                    form,
                    categoryResponse: categoryResponse,
                    autofill: autofill,
                  }}
                />{" "}
              </>
            ) : (
              ""
            )}
          </>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    interestList: state?.eventInterestsData?.data,
    eventData: state.eventData,
    eventSettingsData: state.eventSettingsData,
    countryList: state.countryList,
    categoryResponse: state.categoryResponse,
    checkoutCompanyForm: state.checkoutCompanyForm,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ lastFieldChanged }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(FieldItem);
